'use client';

import { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { createLogger } from '@/shared/lib/logger';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { useImageConstraints } from '@/shared/hooks';
import { rendersService } from '@/shared/api/services/renders.service';
import {
  RenderProgress,
  useSmoothRenderOverlay,
} from '@/features/design/render';
import { AIGlowOverlay } from '@/shared/ui';
import { useIsAnglePreloadingRender } from '@/features/design/job-queue';
import {
  ConceptActions,
  RenameAngleDialog,
  RenderInfoSheet,
} from '@/features/design/concept';
import { AngleImageDisplay } from './AngleImageDisplay';
import { LlmFeedbackImageOverlay } from './LlmFeedbackImageOverlay';
import {
  ANGLE_OVERLAY_BUTTON,
  ANGLE_OVERLAY_PILL,
} from './angle-overlay-chrome';
import {
  RenderFeedbackChips,
  RenderFeedbackBanners,
} from '@/features/design/render/ui/RenderFeedbackGroup';
import { AngleCarouselWithSlider } from './AngleCarouselWithSlider';
import { AngleImageFrame } from './AngleImageFrame';
import { updateAngle } from '@/features/angle/lib/angle-operations';
import { ClassTypes } from '@/shared/constants';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { capitalize } from '@/shared/lib/text';
import type { ConceptAngle, Angle } from '@/types/concept';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';
import type { AngleRenderData } from '../hooks/use-derivative-angle-data';
import type { Job } from '@/features/design/job-queue';
import { isUserVisibleRenderType } from '@/types/render';
import { cn } from '@/shared/lib/cn';
import { useOptionalDesktopLayout } from '@/features/project';
import {
  ConstructedViewPlaceholder,
  ReconstructionMarker,
} from '@/features/project/ui/ReconstructionMarker';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

const logger = createLogger('DerivativeAngleCard');

interface DerivativeAngleCardProps {
  project: Project;
  concept: Concept;
  angle: ConceptAngle;
  angleData: Angle | undefined;
  renderData: AngleRenderData;
  hasMainRender: boolean;
  hideRenderButton?: boolean;
  onAngleUploadSuccess: () => Promise<void>;
  onDeleteAngle: (
    conceptAngleId: string,
    angleId: string | undefined
  ) => Promise<void>;
  onDeleteRender: (renderId: string) => Promise<void>;
  onReplacePhoto: (data: {
    conceptAngleId: string;
    angleName: string;
    hasRenders: boolean;
  }) => void;
  setSelectedRenderId: (angleId: string, renderId: string) => void;
}

function ChevronLeftIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

export function DerivativeAngleCard({
  project,
  concept,
  angle,
  angleData,
  renderData,
  hasMainRender,
  onAngleUploadSuccess,
  onDeleteAngle,
  onDeleteRender,
  onReplacePhoto,
  setSelectedRenderId,
}: DerivativeAngleCardProps) {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tRender = useTranslations('render.options');
  const tAngles = useTranslations('angles');
  const router = useRouter();
  // Server-driven paywall for PROJECT_REFERENCE_LIMIT_EXCEEDED (422).
  // Shared dialog renders the server-owned copy and upgrade/manage actions.
  const routeLimitErrorToPaywall = useLimitErrorPaywall();

  const desktopLayout = useOptionalDesktopLayout();
  const { isXl } = useBreakpoint();
  // Two-column (xl+ inspector) keeps LLM feedback as expanded banners below the
  // image (collapsible to in-flow chips); single-column collapses it to icons
  // pinned on the image. Mirrors `AngleImageFrame`'s own layout signal.
  const isTwoColumnLayout = !!(isXl && desktopLayout);

  // Image sizing constraints (Apple-parity caps/floors) — layout-aware.
  const sizing = useImageConstraints({ isTwoColumn: isTwoColumnLayout });

  // The compare slider is mode-driven on the card that is the active editing
  // target — `selectedAngleId === this.angle.id` — in BOTH arrangements (the
  // selectedAngleId is context-driven and layout-agnostic). There is one angle
  // on screen and it switches as the user toggles Explore/Refine, so the toggle
  // carries no duplicate preview (see UnifiedRenderPanel `hideTogglePreview`).
  // Slider clip semantics (LTR): position 0 = render visible, 100 = source.
  // Refine → 0 (render visible), Recreate/Explore → 100 (source visible).
  // User can still drag manually — drag overrides until the next mode flip.
  const renderMode = useRenderStore((state) => state.formData.mode);
  const isActiveEditingCard = desktopLayout?.selectedAngleId === angle.id;
  const sliderProgrammaticPosition = isActiveEditingCard
    ? renderMode === 'refine'
      ? 0
      : 100
    : undefined;

  const [sliderPosition, setSliderPosition] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSavingAsReference, setIsSavingAsReference] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
  // Ratio the image box is actually shaped to, reported by the display/carousel
  // once the browser decodes the image (Apple `mainImageAspectRatio`).
  const [measuredAspectRatio, setMeasuredAspectRatio] = useState<number | null>(
    null
  );

  const {
    angleRenders,
    completedRenders,
    activeRender,
    completedRenderFromStore,
    activeUpscale,
    allRenders,
    totalRenders,
    currentRender,
    currentIdx,
  } = renderData;

  const hasCompletedRenders = completedRenders.length > 0;
  const angleName =
    angle.relationships?.angle?.meta?.name ||
    tAngles('angleNumbered', { number: angle.attributes.order + 1 });

  const originalImageUrl =
    angle.relationships?.angle?.meta?.file_url ||
    angleData?.attributes?.file?.url ||
    undefined;

  // Constructed view — no source photo, so no "before" tile and no fullscreen
  // original; the empty frame gets an explanation instead of a grey box.
  const hasSourceImage =
    angle.relationships?.angle?.meta?.has_source_image !== false;
  const isReconstruction = project.attributes?.is_reconstruction === true;

  // Seed the container shape from the API-known angle dimensions (concept-angle
  // meta, with the full Angle resource's file dims as a fallback) so it doesn't
  // reflow from 16:9 once the browser decodes the image.
  const angleDimsMeta = angle.relationships?.angle?.meta;
  const angleWidth = angleDimsMeta?.width ?? angleData?.attributes?.file?.width;
  const angleHeight =
    angleDimsMeta?.height ?? angleData?.attributes?.file?.height;
  const initialAspectRatio =
    angleWidth && angleHeight && angleHeight > 0
      ? angleWidth / angleHeight
      : undefined;

  // Shape the card frame matches its strips to (Apple `mainImageAspectRatio`).
  // The loaded image reports it; until then the API-known ratio keeps the frame
  // right on the first paint instead of settling after decode.
  const frameAspectRatio = measuredAspectRatio ?? initialAspectRatio ?? null;

  // Smooth overlay lifecycle (Apple/Android parity): hold the progress
  // overlay until the finished render image is decoded (per-angle preload
  // flag set by the render-completed WS handler), then dissolve over 1.2 s
  // instead of unmounting the instant the job leaves the queue.
  const isPreloadingRender = useIsAnglePreloadingRender(angle.id);
  const { job: overlayJob, phase: overlayPhase } = useSmoothRenderOverlay(
    activeRender ?? activeUpscale,
    isPreloadingRender
  );
  // Un-dim the image exactly when the dissolve starts — the 600 ms
  // grayscale→color reveal of the new render plays under the fading overlay.
  const isImageDimmed = overlayPhase === 'visible' && !!overlayJob;
  const isOverlayPreloading = isPreloadingRender || !(activeRender ?? activeUpscale);
  // Glow follows the overlay, not the queue entry (see AngleImageWithProgress).
  const isGlowActive =
    overlayPhase === 'visible' && !!overlayJob && overlayJob.status !== 'pending';
  // Card chrome (variant pager, actions, feedback) comes back only once the
  // overlay has fully dissolved — popping it in while the card is still dark
  // was the loudest cut in the completion sequence.
  const isOverlayUp = overlayPhase !== 'hidden' && !!overlayJob;

  // DEBUG: Log renders for this angle
  logger.debug('[DERIVATIVE_CARD] Renders for angle:', {
    angleId: angle.id,
    totalInStore: angleRenders.length,
    afterFilter: completedRenders.length,
    allRenders: angleRenders.map((r) => ({
      id: r.id.slice(-8),
      type: r.attributes.render_type,
      status: r.attributes.status,
      isVisible: isUserVisibleRenderType(r.attributes.render_type),
      file: r.relationships?.file?.meta?.url?.slice(-40),
    })),
  });

  // LLM feedback (positive + filtered) — single-column only: icons pinned to
  // the image's bottom-center; tapping opens a modal with the full text (Apple
  // parity). Two-column shows expanded banners below the image instead.
  const feedbackOverlay =
    currentRender && !isOverlayUp && !isTwoColumnLayout ? (
      <LlmFeedbackImageOverlay
        render={currentRender}
        // Pulse the positive-feedback icon for a render that just completed
        // on this screen (the transient completed job entry is the trigger;
        // the overlay latches it for its 4 s window).
        pulseSignal={
          completedRenderFromStore?.jobType === 'render'
            ? completedRenderFromStore.id
            : null
        }
      />
    ) : null;

  const handleSaveAsReference = async () => {
    if (!currentRender?.id) return;
    setIsSavingAsReference(true);
    try {
      await rendersService.saveRenderAsReference(currentRender.id);
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(project.id),
      });
      // Sonner supports `onClick` on the toast body at runtime (used here to
      // navigate to the Brief page — which now hosts references — when the user
      // taps the toast), but the type def for ExternalToast omits it — spread an
      // untyped object so TS doesn't fail excess-property check.
      const navigateToRefs = {
        onClick: () => router.push(`/project/${project.id}/goals`),
      };
      toast.success(tRender('savedAsReference'), {
        style: { cursor: 'pointer' },
        ...navigateToRefs,
      });
    } catch (error) {
      if (routeLimitErrorToPaywall(error)) return;
      if (
        error instanceof ApiClientError &&
        typeof error.message === 'string' &&
        error.message.length > 0
      ) {
        toast.error(error.message);
      } else {
        toast.error(tRender('saveAsReferenceError'));
      }
    } finally {
      setIsSavingAsReference(false);
    }
  };

  // Navigation handlers
  const handlePrevRender = useCallback(() => {
    if (currentIdx > 0) {
      const prevRenderItem = allRenders[currentIdx - 1];
      if (prevRenderItem) {
        setIsTransitioning(true);
        setTimeout(() => {
          setSelectedRenderId(angle.id, prevRenderItem.id);
          setIsTransitioning(false);
        }, 80);
      }
    }
  }, [currentIdx, allRenders, angle.id, setSelectedRenderId]);

  const handleNextRender = useCallback(() => {
    if (currentIdx < totalRenders - 1) {
      const nextRenderItem = allRenders[currentIdx + 1];
      if (nextRenderItem) {
        setIsTransitioning(true);
        setTimeout(() => {
          setSelectedRenderId(angle.id, nextRenderItem.id);
          setIsTransitioning(false);
        }, 80);
      }
    }
  }, [currentIdx, totalRenders, allRenders, angle.id, setSelectedRenderId]);

  // Switching the selected render (carousel paging or a remote/WS selection
  // change) always reveals the render fully — the source photo is identical
  // across every render in the stack, so keeping a dragged-toward-source
  // position would show "nothing changed" between entries. The Explore/Refine
  // programmatic position re-applies only on its own mode flip, so it won't
  // fight this reset. Mirrors Apple/Android.
  useEffect(() => {
    setSliderPosition(0);
  }, [currentRender?.id]);

  // Render progress component
  const renderProgressComponent = (job: Job, isUpscale = false) => (
    <RenderProgress
      {...(isUpscale
        ? { queueRenderInfo: job }
        : {
            render: {
              type: ClassTypes.RENDER,
              id: job.id,
              attributes: {
                status: job.status,
                started_at: job.startedAt || '',
                duration_predicted: job.durationPredicted,
                priority: 0,
                settings: {},
                setup_hash: '',
                created_at: job.startedAt || '',
                updated_at: job.startedAt || '',
              },
            },
          })}
      mode="inline"
      isPreloading={isOverlayPreloading}
      onComplete={() => {}}
      onError={() => {}}
    />
  );

  // Progress overlay — held through image prefetch, then dissolved (duration
  // matches RENDER_OVERLAY_FADE_MS). The retained job keeps the card rendered
  // while it fades even though it has already left the queue.
  const progressOverlay =
    overlayJob && overlayPhase !== 'hidden' ? (
      <div
        className={cn(
          'transition-opacity duration-[1200ms] ease-out',
          overlayPhase === 'fading' ? 'opacity-0' : 'opacity-100'
        )}
      >
        {renderProgressComponent(overlayJob, overlayJob.jobType === 'upscale')}
      </div>
    ) : null;

  // Actions bar content (warning icon + menu). Each button keeps a 44px hit
  // area with a visible 28px circle painted via `before:` pseudo-element so
  // taps land anywhere within the 44px zone instead of just the visible pill.
  const actionsBarContent = (
    <div className="flex items-center">
      {/* Filtered-content feedback now lives as a tappable icon pinned to the
          image's bottom-center (LlmFeedbackImageOverlay). */}
      {!isOverlayUp && (
        <ConceptActions
          projectId={project.id}
          conceptId={concept.id}
          conceptName={capitalize(angleName)}
          conceptSequenceNumber={concept.attributes.project_sequence_number}
          mode="editor"
          isDerivative={true}
          hasRender={hasMainRender}
          renderId={currentRender?.id}
          renderFileUrl={currentRender?.relationships?.file?.meta?.url}
          isUpscaled={currentRender?.attributes?.is_upscaled || false}
          onRenameAngle={() => setShowRenameDialog(true)}
          onReplacePhoto={() =>
            onReplacePhoto({
              conceptAngleId: angle.id,
              angleName: capitalize(angleName),
              hasRenders:
                angleRenders.length > 0 ||
                !!angle.attributes.selected_render_id,
            })
          }
          onDeleteAngle={() =>
            onDeleteAngle(angle.id, angle.relationships?.angle?.data?.id)
          }
          onDeleteRender={(renderId) => onDeleteRender(renderId)}
          onSaveAsReference={handleSaveAsReference}
          isSavingAsReference={isSavingAsReference}
          onShowRenderInfo={
            currentRender ? () => setIsInfoSheetOpen(true) : undefined
          }
          className={cn(
            ANGLE_OVERLAY_BUTTON,
            'hover:before:bg-white/55 dark:hover:before:bg-black/60'
          )}
        />
      )}
    </div>
  );

  // Get store image URL for carousel
  const storeImageUrl =
    completedRenderFromStore?.jobType === 'render' ||
    completedRenderFromStore?.jobType === 'upscale'
      ? completedRenderFromStore.imageUrl
      : null;

  const customAngleName = angle.relationships?.angle?.meta?.name;

  // Overlay pills share the same visible height (28px / h-7) and a 44px
  // (h-11) tap target. Visible pill background is painted via `before:`
  // pseudo-element so each pill stays a single button, not nested wrappers.
  // Shared tokens match Apple (24pt visible / 44pt hit) and Android (28dp /
  // 48dp); pills sit flush with the image corners so the extra hit area
  // extends outward into the corner.

  // Top-leading overlay row: collapsed LLM-feedback icons (single-column only).
  // The custom view name is no longer painted on the image — it lives in the
  // angle tab label now (see ConceptContentTabs). Rename stays in the ⋯ menu.
  const topLeadingOverlay = (
    <div className="absolute top-0 start-0 z-10 flex items-center">
      {feedbackOverlay}
    </div>
  );

  // Permanent "reconstruction, not a photograph" marker for a building that
  // does not exist. Bottom-leading, clear of the feedback icons (top-leading)
  // and the carousel/menu row (top-trailing).
  const reconstructionOverlay = isReconstruction ? (
    <div className="absolute bottom-2 start-2 z-10">
      <ReconstructionMarker compact />
    </div>
  ) : null;

  // Overlay: carousel switcher + menu (top-right). Carousel container is one
  // 44px-tall row; the visible 28px capsule background is painted via
  // `before:` pseudo-element so each chevron button gets the full 44px hit
  // height without nesting buttons inside a smaller capsule.
  const trailingOverlay = (
    <div className="absolute top-0 end-0 z-10 flex items-center">
      {totalRenders > 1 && !isOverlayUp && (
        <div
          className={cn(
            'relative flex h-11 items-center px-1 animate-fade-in',
            "before:absolute before:inset-x-0 before:inset-y-2 before:rounded-full before:content-['']",
            ANGLE_OVERLAY_PILL
          )}
        >
          <button
            onClick={handlePrevRender}
            disabled={currentIdx === 0}
            className="relative inline-flex h-11 w-7 items-center justify-center text-[#545454] dark:text-white transition-opacity disabled:text-gray-400 dark:disabled:text-white/40 disabled:cursor-not-allowed rtl:-scale-x-100"
            title={tCommon('previousVariant')}
          >
            <ChevronLeftIcon />
          </button>

          <span className="relative whitespace-nowrap text-chip text-black dark:text-white tabular-nums">
            {currentIdx + 1}/{totalRenders}
          </span>

          <button
            onClick={handleNextRender}
            disabled={currentIdx === totalRenders - 1}
            className="relative inline-flex h-11 w-7 items-center justify-center text-[#545454] dark:text-white transition-opacity disabled:text-gray-400 dark:disabled:text-white/40 disabled:cursor-not-allowed rtl:-scale-x-100"
            title={tCommon('nextVariant')}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
      {actionsBarContent}
    </div>
  );

  return (
    <div id={`angle-${angle.id}`}>

      {/* Main content - carousel or image. AngleImageFrame is shared with
          MainAngleDisplay so derivative angles get the same frame styling
          (settings-card surface in two-column, tight border in single-column)
          and the same auto-padding behaviour. */}
      {!hasCompletedRenders &&
      !originalImageUrl &&
      !completedRenderFromStore ? (
        <AngleImageFrame className="mb-3">
          <div className="relative rounded-none sm:rounded-lg overflow-hidden">
            {hasSourceImage ? (
              <div className="aspect-[16/9] bg-image-backdrop" />
            ) : (
              <ConstructedViewPlaceholder />
            )}
            {topLeadingOverlay}
            {trailingOverlay}
            {reconstructionOverlay}
          </div>
        </AngleImageFrame>
      ) : storeImageUrl ||
        (hasCompletedRenders && completedRenders.length >= 1) ? (
        <AngleImageFrame
          className="mb-4"
          sizing={sizing}
          imageAspectRatio={frameAspectRatio}
        >
          <div className="relative rounded-none sm:rounded-lg overflow-hidden">
            {topLeadingOverlay}
            {trailingOverlay}
            {reconstructionOverlay}
            <AngleCarouselWithSlider
              sourceUrl={originalImageUrl}
              renderUrls={[
                ...(completedRenders
                  .map((r) => r.relationships?.file?.meta?.url)
                  .filter(Boolean) as string[]),
                ...(storeImageUrl ? [storeImageUrl] : []),
              ].filter((url, index, self) => self.indexOf(url) === index)}
              alt={angleName}
              currentIndex={currentIdx}
              onIndexChange={(newIndex) => {
                const selectedRenderItem = allRenders[newIndex];
                if (selectedRenderItem) {
                  setSelectedRenderId(angle.id, selectedRenderItem.id);
                }
              }}
              sliderPosition={sliderPosition}
              onSliderPositionChange={setSliderPosition}
              isTransitioning={isTransitioning}
              containerClassName=""
              showNavigation={false}
              isUpscaled={currentRender?.attributes?.is_upscaled || false}
              isRendering={isImageDimmed}
              onSwipeLeft={handleNextRender}
              onSwipeRight={handlePrevRender}
              onImageClick={() => setViewerOpen(true)}
              isViewerOpenExternal={viewerOpen}
              onViewerClose={() => setViewerOpen(false)}
              sizing={sizing}
              sliderProgrammaticPosition={sliderProgrammaticPosition}
              initialAspectRatio={initialAspectRatio}
              onAspectRatioChange={setMeasuredAspectRatio}
            />

            <AIGlowOverlay isActive={isGlowActive} />
            {progressOverlay}
          </div>
        </AngleImageFrame>
      ) : originalImageUrl ? (
        <AngleImageFrame
          className="mb-4"
          sizing={sizing}
          imageAspectRatio={frameAspectRatio}
        >
          <div className="relative rounded-none sm:rounded-lg overflow-hidden">
            {topLeadingOverlay}
            {trailingOverlay}
            {reconstructionOverlay}
            <AngleImageDisplay
              key={originalImageUrl}
              sourceUrl={originalImageUrl}
              renderUrl={undefined}
              alt={angleName}
              containerClassName=""
              isRendering={isImageDimmed}
              onImageClick={() => setViewerOpen(true)}
              isViewerOpenExternal={viewerOpen}
              onViewerClose={() => setViewerOpen(false)}
              sizing={sizing}
              initialAspectRatio={initialAspectRatio}
              onAspectRatioChange={setMeasuredAspectRatio}
            />

            <AIGlowOverlay isActive={isGlowActive} />
            {progressOverlay}
          </div>
        </AngleImageFrame>
      ) : (
        <AngleImageFrame className="mb-4">
          <div className="relative rounded-none sm:rounded-lg overflow-hidden">
            <div className="aspect-[16/9] bg-image-backdrop" />
            {topLeadingOverlay}
            {trailingOverlay}
            {reconstructionOverlay}
          </div>
        </AngleImageFrame>
      )}

      {/* LLM feedback (two-column) — expanded banners below the image,
          collapsible to in-flow chips above them. Single-column shows icons
          in the image's top-leading row instead (topLeadingOverlay above). */}
      {isTwoColumnLayout && currentRender && !isOverlayUp && (
        <div className="mb-4 flex animate-fade-in flex-col gap-2">
          <RenderFeedbackChips render={currentRender} />
          <RenderFeedbackBanners render={currentRender} />
        </div>
      )}

      {/* Render button moved to the floating Generate capsule (single-column)
          / inspector footer (two-column). */}

      <RenameAngleDialog
        angleId={angleData?.id ?? null}
        currentName={customAngleName ?? null}
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        onSave={async (angleId, newName) => {
          await updateAngle(angleId, newName.length > 0 ? newName : null);
          await onAngleUploadSuccess();
        }}
      />

      {/* Render-info sheet — opened via the "Information" item in the
          angle's overflow menu. Derivative refines stay on the same
          concept_angle, so swap-in-place is the common branch;
          cross-concept navigation is the fallback when the parent lives
          elsewhere (rare but possible after concept moves). */}
      {currentRender && (
        <RenderInfoSheet
          render={currentRender}
          projectId={project.id}
          currentAngleId={angle.id}
          open={isInfoSheetOpen}
          onOpenChange={setIsInfoSheetOpen}
          onSwapToParent={(parentRenderId) => {
            setSelectedRenderId(angle.id, parentRenderId);
          }}
          onNavigateToParent={(parentConceptId, parentAngleId) => {
            const url = parentAngleId
              ? `/project/${project.id}/concept/${parentConceptId}?angleId=${parentAngleId}`
              : `/project/${project.id}/concept/${parentConceptId}`;
            router.push(url);
          }}
        />
      )}
    </div>
  );
}
